/* @ts-self-types="./webrace_core.d.ts" */

/**
 * OR additional surface flags onto a shader (e.g. client-discovered
 * `surfaceparm slick` that isn't in the BSP's shader flags). Must be called
 * before `session_new` so the collision world picks it up.
 * @param {number} id
 * @param {number} shader_index
 * @param {number} flags
 */
export function bsp_add_shader_flags(id, shader_index, flags) {
    wasm.bsp_add_shader_flags(id, shader_index, flags);
}

/**
 * @param {number} id
 * @returns {number}
 */
export function bsp_brush_count(id) {
    const ret = wasm.bsp_brush_count(id);
    return ret >>> 0;
}

/**
 * Shader index of collision brush `i` (parallel to `bsp_brush_count`).
 * @param {number} id
 * @param {number} i
 * @returns {number}
 */
export function bsp_brush_shader(id, i) {
    const ret = wasm.bsp_brush_shader(id, i);
    return ret;
}

/**
 * @param {number} id
 * @returns {number}
 */
export function bsp_chunk_count(id) {
    const ret = wasm.bsp_chunk_count(id);
    return ret >>> 0;
}

/**
 * Write chunk data into JS-provided arrays: (shader_index, first_index,
 * index_count) triplets. `shader_idx`, `first`, `count` are pre-allocated
 * `Uint32Array`s of length chunk_count.
 * @param {number} id
 * @param {Uint32Array} shader_idx
 * @param {Uint32Array} first
 * @param {Uint32Array} count
 */
export function bsp_chunks(id, shader_idx, first, count) {
    var ptr0 = passArray32ToWasm0(shader_idx, wasm.__wbindgen_malloc);
    var len0 = WASM_VECTOR_LEN;
    var ptr1 = passArray32ToWasm0(first, wasm.__wbindgen_malloc);
    var len1 = WASM_VECTOR_LEN;
    var ptr2 = passArray32ToWasm0(count, wasm.__wbindgen_malloc);
    var len2 = WASM_VECTOR_LEN;
    wasm.bsp_chunks(id, ptr0, len0, shader_idx, ptr1, len1, first, ptr2, len2, count);
}

/**
 * Diagnostic: map a *world* render triangle index back to the face that owns
 * it. `face_ranges` tile the world index buffer contiguously in emission
 * order, so a binary search finds the owning render face.
 *
 * Writes `out = [render_face, bsp_face, shader_index, is_patch]` and returns
 * 0, or -1 when the map/triangle is out of range. `bsp_face` is the
 * `LUMP_FACES` index (`-1` if none maps back); `is_patch` is 1.0 for a bezier
 * (`FACETYPE_PATCH`) face. Used by the client's face-probe debug hook.
 * @param {number} id
 * @param {number} tri
 * @param {Float32Array} out
 * @returns {number}
 */
export function bsp_face_at_triangle(id, tri, out) {
    var ptr0 = passArrayF32ToWasm0(out, wasm.__wbindgen_malloc);
    var len0 = WASM_VECTOR_LEN;
    const ret = wasm.bsp_face_at_triangle(id, tri, ptr0, len0, out);
    return ret;
}

/**
 * Shader index of the fog volume (`CONTENTS_FOG` brush) containing `eye`, or
 * **-1** when the camera is outside every fog volume. The client looks up that
 * shader's `fogparms` and applies scene fog while inside the volume.
 * @param {number} id
 * @param {Float32Array} eye
 * @returns {number}
 */
export function bsp_fog_at(id, eye) {
    const ptr0 = passArrayF32ToWasm0(eye, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.bsp_fog_at(id, ptr0, len0);
    return ret;
}

/**
 * @param {number} id
 * @returns {number}
 */
export function bsp_index_count(id) {
    const ret = wasm.bsp_index_count(id);
    return ret >>> 0;
}

/**
 * @param {number} id
 * @returns {number}
 */
export function bsp_indices_ptr(id) {
    const ret = wasm.bsp_indices_ptr(id);
    return ret >>> 0;
}

/**
 * Item `i` as `[x, y, z, kind, weapon, amount]` into `out` (len >= 6).
 * @param {number} id
 * @param {number} i
 * @param {Float32Array} out
 */
export function bsp_item(id, i, out) {
    var ptr0 = passArrayF32ToWasm0(out, wasm.__wbindgen_malloc);
    var len0 = WASM_VECTOR_LEN;
    wasm.bsp_item(id, i, ptr0, len0, out);
}

/**
 * @param {number} id
 * @returns {number}
 */
export function bsp_item_count(id) {
    const ret = wasm.bsp_item_count(id);
    return ret >>> 0;
}

/**
 * Number of jumppad (`trigger_push`) volumes parsed from the map.
 * @param {number} id
 * @returns {number}
 */
export function bsp_jumppad_count(id) {
    const ret = wasm.bsp_jumppad_count(id);
    return ret >>> 0;
}

/**
 * Trilinearly sample the baked lightgrid at a world point (QFusion
 * `R_LightForOrigin`). Writes 6 floats into `out` as `ambient.rgb,
 * diffuse.rgb` in raw 0..255 units, then returns 0. Returns -1 if the map has
 * no usable lightgrid (no lump, or a stale `gridsize` that doesn't match it).
 * @param {number} id
 * @param {number} x
 * @param {number} y
 * @param {number} z
 * @param {Float32Array} out
 * @returns {number}
 */
export function bsp_light_at(id, x, y, z, out) {
    var ptr0 = passArrayF32ToWasm0(out, wasm.__wbindgen_malloc);
    var len0 = WASM_VECTOR_LEN;
    const ret = wasm.bsp_light_at(id, x, y, z, ptr0, len0, out);
    return ret;
}

/**
 * @param {number} id
 * @returns {number}
 */
export function bsp_lightmap_h(id) {
    const ret = wasm.bsp_lightmap_h(id);
    return ret >>> 0;
}

/**
 * @param {number} id
 * @returns {number}
 */
export function bsp_lightmap_len(id) {
    const ret = wasm.bsp_lightmap_len(id);
    return ret >>> 0;
}

/**
 * Byte offset into WASM memory of the packed RGB lightmap atlas.
 * @param {number} id
 * @returns {number}
 */
export function bsp_lightmap_ptr(id) {
    const ret = wasm.bsp_lightmap_ptr(id);
    return ret >>> 0;
}

/**
 * @param {number} id
 * @returns {number}
 */
export function bsp_lightmap_w(id) {
    const ret = wasm.bsp_lightmap_w(id);
    return ret >>> 0;
}

/**
 * @param {number} id
 * @returns {number}
 */
export function bsp_mover_chunk_count(id) {
    const ret = wasm.bsp_mover_chunk_count(id);
    return ret >>> 0;
}

/**
 * Write mover draw chunks: `(mover_index, shader_index, first_index, count)`.
 * @param {number} id
 * @param {Uint32Array} mover_out
 * @param {Uint32Array} shader_out
 * @param {Uint32Array} first
 * @param {Uint32Array} count
 */
export function bsp_mover_chunks(id, mover_out, shader_out, first, count) {
    var ptr0 = passArray32ToWasm0(mover_out, wasm.__wbindgen_malloc);
    var len0 = WASM_VECTOR_LEN;
    var ptr1 = passArray32ToWasm0(shader_out, wasm.__wbindgen_malloc);
    var len1 = WASM_VECTOR_LEN;
    var ptr2 = passArray32ToWasm0(first, wasm.__wbindgen_malloc);
    var len2 = WASM_VECTOR_LEN;
    var ptr3 = passArray32ToWasm0(count, wasm.__wbindgen_malloc);
    var len3 = WASM_VECTOR_LEN;
    wasm.bsp_mover_chunks(id, ptr0, len0, mover_out, ptr1, len1, shader_out, ptr2, len2, first, ptr3, len3, count);
}

/**
 * Number of animated solid brush entities (`func_bobbing`/`func_plat`/...).
 * @param {number} id
 * @returns {number}
 */
export function bsp_mover_count(id) {
    const ret = wasm.bsp_mover_count(id);
    return ret >>> 0;
}

/**
 * @param {number} id
 * @returns {number}
 */
export function bsp_mover_index_count(id) {
    const ret = wasm.bsp_mover_index_count(id);
    return ret >>> 0;
}

/**
 * @param {number} id
 * @returns {number}
 */
export function bsp_mover_indices_ptr(id) {
    const ret = wasm.bsp_mover_indices_ptr(id);
    return ret >>> 0;
}

/**
 * @param {number} id
 * @returns {number}
 */
export function bsp_mover_vertex_count(id) {
    const ret = wasm.bsp_mover_vertex_count(id);
    return ret >>> 0;
}

/**
 * Byte offset into WASM memory of the mover render vertices (14 f32 each).
 * @param {number} id
 * @returns {number}
 */
export function bsp_mover_vertices_ptr(id) {
    const ret = wasm.bsp_mover_vertices_ptr(id);
    return ret >>> 0;
}

/**
 * @param {string} name
 * @param {Uint8Array} data
 * @returns {number}
 */
export function bsp_parse(name, data) {
    const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(data, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.bsp_parse(ptr0, len0, ptr1, len1);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0] >>> 0;
}

/**
 * Completely drop all loaded maps and reset the thread-local store.
 * Release every parsed BSP handle. The server calls this before parsing a new
 * map: a built `World` owns its collision data, so the old handle is no longer
 * needed. (Handle indices are not reused, so a released slot is just dropped.)
 */
export function bsp_release_all() {
    wasm.bsp_release_all();
}

/**
 * @param {number} id
 * @returns {number}
 */
export function bsp_shader_count(id) {
    const ret = wasm.bsp_shader_count(id);
    return ret >>> 0;
}

/**
 * Copy each shader's surface flags into `out` (parallel to `bsp_shader_name`).
 * @param {number} id
 * @param {Int32Array} out
 */
export function bsp_shader_flags(id, out) {
    var ptr0 = passArray32ToWasm0(out, wasm.__wbindgen_malloc);
    var len0 = WASM_VECTOR_LEN;
    wasm.bsp_shader_flags(id, ptr0, len0, out);
}

/**
 * Return the shader name for a given shader index (empty if out of range).
 * @param {number} id
 * @param {number} shader_index
 * @returns {string}
 */
export function bsp_shader_name(id, shader_index) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.bsp_shader_name(id, shader_index);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Diagnostic (the "stuck" probe): the collision-brush volumes whose convex
 * hull contains the player AABB `origin+mins .. origin+maxs`. Writes brush
 * indices into `out` and returns the count (which may exceed `out.len()`).
 * Combined with `bsp_brush_shader` / `bsp_shader_name` this says exactly which
 * brush geometry the player is embedded in.
 * @param {number} id
 * @param {Float32Array} origin
 * @param {Float32Array} mins
 * @param {Float32Array} maxs
 * @param {Float32Array} out
 * @returns {number}
 */
export function bsp_solid_brushes_overlapping(id, origin, mins, maxs, out) {
    const ptr0 = passArrayF32ToWasm0(origin, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArrayF32ToWasm0(mins, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArrayF32ToWasm0(maxs, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    var ptr3 = passArrayF32ToWasm0(out, wasm.__wbindgen_malloc);
    var len3 = WASM_VECTOR_LEN;
    const ret = wasm.bsp_solid_brushes_overlapping(id, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, out);
    return ret >>> 0;
}

/**
 * @param {number} id
 * @returns {number}
 */
export function bsp_spawn_count(id) {
    const ret = wasm.bsp_spawn_count(id);
    return ret >>> 0;
}

/**
 * Teleporter `i` as `[dest x, dest y, dest z, trigger centre x, y, z]` into
 * `out` (>=6). Returns false if out of range. Client uses it to draw a marker at
 * each teleporter's destination.
 * @param {number} id
 * @param {number} i
 * @param {Float32Array} out
 * @returns {boolean}
 */
export function bsp_teleporter(id, i, out) {
    var ptr0 = passArrayF32ToWasm0(out, wasm.__wbindgen_malloc);
    var len0 = WASM_VECTOR_LEN;
    const ret = wasm.bsp_teleporter(id, i, ptr0, len0, out);
    return ret !== 0;
}

/**
 * Number of teleporter (`trigger_teleport`) volumes parsed from the map.
 * @param {number} id
 * @returns {number}
 */
export function bsp_teleporter_count(id) {
    const ret = wasm.bsp_teleporter_count(id);
    return ret >>> 0;
}

/**
 * @param {number} id
 * @returns {number}
 */
export function bsp_triangle_count(id) {
    const ret = wasm.bsp_triangle_count(id);
    return ret >>> 0;
}

/**
 * Diagnostic: number of collision patch/displacement triangles the player box
 * at `origin` **penetrates** (not just AABB-overlaps). A non-zero count means
 * the cur/curve collision is wedging the player.
 * @param {number} id
 * @param {Float32Array} origin
 * @param {Float32Array} mins
 * @param {Float32Array} maxs
 * @returns {number}
 */
export function bsp_tris_overlapping(id, origin, mins, maxs) {
    const ptr0 = passArrayF32ToWasm0(origin, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArrayF32ToWasm0(mins, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArrayF32ToWasm0(maxs, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.bsp_tris_overlapping(id, ptr0, len0, ptr1, len1, ptr2, len2);
    return ret >>> 0;
}

/**
 * Number of `trigger_push_velocity` (defrag/racemod velocity pad) volumes.
 * @param {number} id
 * @returns {number}
 */
export function bsp_velocity_pad_count(id) {
    const ret = wasm.bsp_velocity_pad_count(id);
    return ret >>> 0;
}

/**
 * @param {number} id
 * @returns {number}
 */
export function bsp_vertex_count(id) {
    const ret = wasm.bsp_vertex_count(id);
    return ret >>> 0;
}

/**
 * Byte offset into WASM memory of the render vertices (14 f32 per vertex).
 * @param {number} id
 * @returns {number}
 */
export function bsp_vertices_ptr(id) {
    const ret = wasm.bsp_vertices_ptr(id);
    return ret >>> 0;
}

/**
 * Compute the PVS-visible draw runs for a camera at `eye` and write flat
 * `(shader_index, first_index, index_count)` triples into `out`.
 *
 * Returns the number of runs, or **-1** to mean "draw everything" (caller
 * should fall back to the full per-shader chunks): no visibility lump, the
 * eye is in cluster 0 / a solid leaf, nothing is visible, or `out` is too
 * small. Allocate at least `bsp_world_face_count(id) * 3` u32s.
 *
 * `planes`, when it holds 24 floats, is the view frustum as 6 planes
 * (`[a,b,c,d]`, inside = `a*x+b*y+c*z+d >= 0`); faces whose AABB is fully
 * outside are culled on top of PVS. A shorter slice disables frustum culling.
 * Returns **0** when the PVS matched but every face was frustum-culled (draw
 * nothing) — distinct from -1 ("draw everything").
 * @param {number} id
 * @param {Float32Array} eye
 * @param {Float32Array} planes
 * @param {Uint32Array} out
 * @returns {number}
 */
export function bsp_visible_runs(id, eye, planes, out) {
    const ptr0 = passArrayF32ToWasm0(eye, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArrayF32ToWasm0(planes, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    var ptr2 = passArray32ToWasm0(out, wasm.__wbindgen_malloc);
    var len2 = WASM_VECTOR_LEN;
    const ret = wasm.bsp_visible_runs(id, ptr0, len0, ptr1, len1, ptr2, len2, out);
    return ret;
}

/**
 * Number of drawable *world* faces (the granularity PVS culls at).
 * @param {number} id
 * @returns {number}
 */
export function bsp_world_face_count(id) {
    const ret = wasm.bsp_world_face_count(id);
    return ret >>> 0;
}

/**
 * WASM-facing hex fingerprint (Node can compare strings without BigInt).
 * @returns {string}
 */
export function determinism_fingerprint_hex() {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.determinism_fingerprint_hex();
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Exported to JS: the canonical gameplay event bit table. The client generator
 * emits `web/src/net/events.ts` from this, so TS never re-declares the shifts.
 * @returns {string}
 */
export function event_schema() {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.event_schema();
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Built without a `#[wasm_bindgen(start)]` entrypoint; the first public
 * exported function call is the entry. A panic hook is installed lazily in
 * JS by calling `install_panic_hook` (below) in `main.ts`.
 */
export function install_panic_hook() {
    wasm.install_panic_hook();
}

/**
 * Decompress a standard `.lzma` buffer (props + size + stream). Used by the
 * map-server to read LZMA-compressed entries (ZIP method 14) inside a map's
 * embedded PAKFILE, and zstd is handled by Node's zlib.
 * @param {Uint8Array} data
 * @returns {Uint8Array}
 */
export function lzma_decompress(data) {
    const ptr0 = passArray8ToWasm0(data, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.lzma_decompress(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * @param {number} world
 * @param {number} spawn
 * @returns {number}
 */
export function networld_add_player(world, spawn) {
    const ret = wasm.networld_add_player(world, spawn);
    return ret;
}

/**
 * Live hitscan beams, flattened at `Beam::STRIDE` floats each.
 * @param {number} world
 * @returns {Float32Array}
 */
export function networld_beams(world) {
    const ret = wasm.networld_beams(world);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Free a world handle (releases its map data). The handle becomes invalid.
 * @param {number} world
 */
export function networld_drop(world) {
    wasm.networld_drop(world);
}

/**
 * All live entities, flattened at `Entity::STRIDE` floats each.
 * @param {number} world
 * @returns {Float32Array}
 */
export function networld_entities(world) {
    const ret = wasm.networld_entities(world);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * CTF flag states: 8 floats per team `[team, state, carrier, x, y, z,
 * return_ms, present]` (state 0 home, 1 carried, 2 dropped).
 * @param {number} world
 * @returns {Float32Array}
 */
export function networld_flags(world) {
    const ret = wasm.networld_flags(world);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Active heal bubbles: `[x,y,z,radius,team,remaining_ms]` each.
 * @param {number} world
 * @returns {Float32Array}
 */
export function networld_heal_zones(world) {
    const ret = wasm.networld_heal_zones(world);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * PVS interest mask for `viewer`: 1.0 = send that player slot, 0.0 = cull.
 * Empty when there is no world (the server then sends everyone).
 * @param {number} world
 * @param {number} viewer
 * @returns {Float32Array}
 */
export function networld_interest_mask(world, viewer) {
    const ret = wasm.networld_interest_mask(world, viewer);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Full item list: `[x,y,z,kind,weapon,amount,present]` per item.
 * @param {number} world
 * @returns {Float32Array}
 */
export function networld_items(world) {
    const ret = wasm.networld_items(world);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Lag-compensation rewind (ticks) for a shot: full RTT + interpolation delay,
 * capped. The server computes this per command (see `docs/netcode-research.md`).
 * @param {number} ping_ms
 * @param {number} interp_ms
 * @returns {number}
 */
export function networld_lag_ticks(ping_ms, interp_ms) {
    const ret = wasm.networld_lag_ticks(ping_ms, interp_ms);
    return ret >>> 0;
}

/**
 * `[phase, score1, score2, winner_team, winner_slot, time_left_ms,
 *   golden_frag, elapsed_ms, overtime_active]`.
 * @param {number} world
 * @returns {Float32Array}
 */
export function networld_match_state(world) {
    const ret = wasm.networld_match_state(world);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * The lag-compensation rewind cap (ticks), so the server and core agree.
 * @returns {number}
 */
export function networld_max_lag_ticks() {
    const ret = wasm.networld_max_lag_ticks();
    return ret >>> 0;
}

/**
 * Deepest queued-command backlog across players (diagnostics).
 * @param {number} world
 * @returns {number}
 */
export function networld_max_queue(world) {
    const ret = wasm.networld_max_queue(world);
    return ret >>> 0;
}

/**
 * Canonical game-mode dump.
 * @param {number} world
 * @returns {string}
 */
export function networld_mode_dump(world) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.networld_mode_dump(world);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * FNV-1a hash of the world's canonical game-mode dump.
 * @param {number} world
 * @returns {number}
 */
export function networld_mode_hash(world) {
    const ret = wasm.networld_mode_hash(world);
    return ret >>> 0;
}

/**
 * Canonical dump for a built-in preset id (`race`, `duel`, `wipeout`, …).
 * @param {string} id
 * @returns {string}
 */
export function networld_mode_preset_dump(id) {
    let deferred2_0;
    let deferred2_1;
    try {
        const ptr0 = passStringToWasm0(id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.networld_mode_preset_dump(ptr0, len0);
        deferred2_0 = ret[0];
        deferred2_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
    }
}

/**
 * Human label for a built-in preset id.
 * @param {string} id
 * @returns {string}
 */
export function networld_mode_preset_label(id) {
    let deferred2_0;
    let deferred2_1;
    try {
        const ptr0 = passStringToWasm0(id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.networld_mode_preset_label(ptr0, len0);
        deferred2_0 = ret[0];
        deferred2_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
    }
}

/**
 * Whether a string is a well-formed game-mode dump.
 * @param {string} dump
 * @returns {boolean}
 */
export function networld_mode_valid(dump) {
    const ptr0 = passStringToWasm0(dump, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.networld_mode_valid(ptr0, len0);
    return ret !== 0;
}

/**
 * Create a shared world for a parsed map. Returns a handle, or -1 on failure.
 * @param {number} map_id
 * @returns {number}
 */
export function networld_new(map_id) {
    const ret = wasm.networld_new(map_id);
    return ret;
}

/**
 * Last command seq applied for `slot`.
 * @param {number} world
 * @param {number} slot
 * @returns {number}
 */
export function networld_player_ack(world, slot) {
    const ret = wasm.networld_player_ack(world, slot);
    return ret >>> 0;
}

/**
 * `[health, armor, weapon, ammo, events, strong, alive]` for the HUD.
 * @param {number} world
 * @param {number} slot
 * @returns {Float32Array}
 */
export function networld_player_combat(world, slot) {
    const ret = wasm.networld_player_combat(world, slot);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * @param {number} world
 * @returns {number}
 */
export function networld_player_count(world) {
    const ret = wasm.networld_player_count(world);
    return ret >>> 0;
}

/**
 * Authoritative `PlayerState` bytes after the last applied command (empty if
 * none) — sent to the owning client for reconciliation.
 * @param {number} world
 * @param {number} slot
 * @returns {Uint8Array}
 */
export function networld_player_ps(world, slot) {
    const ret = wasm.networld_player_ps(world, slot);
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * @param {number} world
 * @param {number} slot
 * @returns {Float32Array}
 */
export function networld_player_state(world, slot) {
    const ret = wasm.networld_player_state(world, slot);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Save the player's current position as its respawn point (multiplayer save).
 * @param {number} world
 * @param {number} slot
 */
export function networld_position_save(world, slot) {
    wasm.networld_position_save(world, slot);
}

/**
 * Queue a client command: move axes (-127..127), buttons bitfield, 16-bit
 * view angles.
 * @param {number} world
 * @param {number} slot
 * @param {number} seq
 * @param {number} lag_ticks
 * @param {number} forward
 * @param {number} right
 * @param {number} up
 * @param {number} buttons
 * @param {number} yaw
 * @param {number} pitch
 */
export function networld_push_input(world, slot, seq, lag_ticks, forward, right, up, buttons, yaw, pitch) {
    wasm.networld_push_input(world, slot, seq, lag_ticks, forward, right, up, buttons, yaw, pitch);
}

/**
 * As [`networld_push_input_stamped`], plus the shooter's own rendered camera
 * pose (`eye_x/y/z`, `pose_yaw`, `pose_pitch`). The server validates it
 * against the predicted state before using it for the shot origin/angles.
 * @param {number} world
 * @param {number} slot
 * @param {number} seq
 * @param {number} lag_ticks
 * @param {number} stamp_a
 * @param {number} stamp_b
 * @param {number} stamp_f
 * @param {number} eye_x
 * @param {number} eye_y
 * @param {number} eye_z
 * @param {number} pose_yaw
 * @param {number} pose_pitch
 * @param {number} forward
 * @param {number} right
 * @param {number} up
 * @param {number} buttons
 * @param {number} yaw
 * @param {number} pitch
 */
export function networld_push_input_posed(world, slot, seq, lag_ticks, stamp_a, stamp_b, stamp_f, eye_x, eye_y, eye_z, pose_yaw, pose_pitch, forward, right, up, buttons, yaw, pitch) {
    wasm.networld_push_input_posed(world, slot, seq, lag_ticks, stamp_a, stamp_b, stamp_f, eye_x, eye_y, eye_z, pose_yaw, pose_pitch, forward, right, up, buttons, yaw, pitch);
}

/**
 * As [`networld_push_input`], plus the client's rendered render stamp
 * (`stamp_a`, `stamp_b`, `stamp_f` 0..=65535) so the shot is resolved against
 * the exact interpolated scene the shooter saw.
 * @param {number} world
 * @param {number} slot
 * @param {number} seq
 * @param {number} lag_ticks
 * @param {number} stamp_a
 * @param {number} stamp_b
 * @param {number} stamp_f
 * @param {number} forward
 * @param {number} right
 * @param {number} up
 * @param {number} buttons
 * @param {number} yaw
 * @param {number} pitch
 */
export function networld_push_input_stamped(world, slot, seq, lag_ticks, stamp_a, stamp_b, stamp_f, forward, right, up, buttons, yaw, pitch) {
    wasm.networld_push_input_stamped(world, slot, seq, lag_ticks, stamp_a, stamp_b, stamp_f, forward, right, up, buttons, yaw, pitch);
}

/**
 * @param {number} world
 * @returns {boolean}
 */
export function networld_pvp(world) {
    const ret = wasm.networld_pvp(world);
    return ret !== 0;
}

/**
 * @param {number} world
 * @param {number} slot
 */
export function networld_remove_player(world, slot) {
    wasm.networld_remove_player(world, slot);
}

/**
 * Reset the match to warmup (scores/stats/spawns/items).
 * @param {number} world
 */
export function networld_reset_match(world) {
    wasm.networld_reset_match(world);
}

/**
 * Respawn the player at its saved spawn with zero velocity (multiplayer restart).
 * @param {number} world
 * @param {number} slot
 */
export function networld_restart(world, slot) {
    wasm.networld_restart(world, slot);
}

/**
 * Number of canonical rule keys (client sanity-check).
 * @returns {number}
 */
export function networld_rules_count() {
    const ret = wasm.networld_rules_count();
    return ret >>> 0;
}

/**
 * Canonical ruleset dump (for debugging the client/server hash match).
 * @param {number} world
 * @returns {string}
 */
export function networld_rules_dump(world) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.networld_rules_dump(world);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * FNV-1a hash of the world's canonical ruleset (see `Ruleset::hash`).
 * @param {number} world
 * @returns {number}
 */
export function networld_rules_hash(world) {
    const ret = wasm.networld_rules_hash(world);
    return ret >>> 0;
}

/**
 * Whether `viewer` should receive a point entity at `(x, y, z)` (1 = send).
 * @param {number} world
 * @param {number} viewer
 * @param {number} x
 * @param {number} y
 * @param {number} z
 * @returns {number}
 */
export function networld_sees_point(world, viewer, x, y, z) {
    const ret = wasm.networld_sees_point(world, viewer, x, y, z);
    return ret;
}

/**
 * Select a weapon for a player (`strong` = primary fire mode).
 * @param {number} world
 * @param {number} slot
 * @param {number} weapon
 * @param {boolean} strong
 */
export function networld_select_weapon(world, slot, weapon, strong) {
    wasm.networld_select_weapon(world, slot, weapon, strong);
}

/**
 * Apply a game-mode dump. Returns false for a malformed dump.
 * @param {number} world
 * @param {string} dump
 * @returns {boolean}
 */
export function networld_set_mode(world, dump) {
    const ptr0 = passStringToWasm0(dump, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.networld_set_mode(world, ptr0, len0);
    return ret !== 0;
}

/**
 * Enable/disable player-vs-player collision (votable game option).
 * @param {number} world
 * @param {boolean} on
 */
export function networld_set_pvp(world, on) {
    wasm.networld_set_pvp(world, on);
}

/**
 * Set a player's warmup ready flag.
 * @param {number} world
 * @param {number} slot
 * @param {boolean} ready
 */
export function networld_set_ready(world, slot, ready) {
    wasm.networld_set_ready(world, slot, ready);
}

/**
 * Apply one dev-tab rule (`key` -> `value`). Returns false for unknown keys.
 * @param {number} world
 * @param {string} key
 * @param {number} value
 * @returns {boolean}
 */
export function networld_set_rule(world, key, value) {
    const ptr0 = passStringToWasm0(key, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.networld_set_rule(world, ptr0, len0, value);
    return ret !== 0;
}

/**
 * Assign a player's team (0 = none/FFA, 1 = red, 2 = blue).
 * @param {number} world
 * @param {number} slot
 * @param {number} team
 */
export function networld_set_team(world, slot, team) {
    wasm.networld_set_team(world, slot, team);
}

/**
 * Spawn a physics ball at `(x,y,z)` with velocity `(vx,vy,vz)`. Debug/dev hook
 * (used by the client's ball-throw bind). Returns the entity id (0 if the
 * world handle is invalid).
 * @param {number} world
 * @param {number} x
 * @param {number} y
 * @param {number} z
 * @param {number} vx
 * @param {number} vy
 * @param {number} vz
 * @returns {number}
 */
export function networld_spawn_ball(world, x, y, z, vx, vy, vz) {
    const ret = wasm.networld_spawn_ball(world, x, y, z, vx, vy, vz);
    return ret >>> 0;
}

/**
 * @param {number} world
 */
export function networld_step(world) {
    wasm.networld_step(world);
}

/**
 * Move a player's team without the death event/sound (team-switch command).
 * @param {number} world
 * @param {number} slot
 * @param {number} team
 */
export function networld_switch_team(world, slot, team) {
    wasm.networld_switch_team(world, slot, team);
}

/**
 * Captures since the last call: `[team, slot, ...]`.
 * @param {number} world
 * @returns {Float32Array}
 */
export function networld_take_captures(world) {
    const ret = wasm.networld_take_captures(world);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Damage applications since the last call, stride 12:
 * `[tick, attacker, victim, weapon, damage, applied, friendly, died,
 *   attacker_team, victim_team, victim_health, victim_armor]`.
 * @param {number} world
 * @returns {Float32Array}
 */
export function networld_take_damage(world) {
    const ret = wasm.networld_take_damage(world);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Read + clear a player's pending combat event bits.
 * @param {number} world
 * @param {number} slot
 * @returns {number}
 */
export function networld_take_events(world, slot) {
    const ret = wasm.networld_take_events(world, slot);
    return ret >>> 0;
}

/**
 * Item-presence changes since the last call: `[index, present, ...]`.
 * @param {number} world
 * @returns {Float32Array}
 */
export function networld_take_item_changes(world) {
    const ret = wasm.networld_take_item_changes(world);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Deaths since the last call: `[killer, victim, weapon, team_kill]` per kill
 * (killer = 255 for suicide/world).
 * @param {number} world
 * @returns {Float32Array}
 */
export function networld_take_kills(world) {
    const ret = wasm.networld_take_kills(world);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Beams created since the last call (stride 9: owner, weapon, start x3, end x3,
 * remaining_ms). Each beam is returned once; the client ages it locally.
 * @param {number} world
 * @returns {Float32Array}
 */
export function networld_take_new_beams(world) {
    const ret = wasm.networld_take_new_beams(world);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Shots since the last call, stride 13:
 * `[tick, shooter, weapon, kind, hit, victim, sx,sy,sz, dx,dy,dz, lag]`.
 * @param {number} world
 * @returns {Float32Array}
 */
export function networld_take_shots(world) {
    const ret = wasm.networld_take_shots(world);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Teleport a player to an origin (zero velocity). Tests / admin only.
 * @param {number} world
 * @param {number} slot
 * @param {number} x
 * @param {number} y
 * @param {number} z
 */
export function networld_teleport(world, slot, x, y, z) {
    wasm.networld_teleport(world, slot, x, y, z);
}

/**
 * @param {number} world
 * @returns {number}
 */
export function networld_tick(world) {
    const ret = wasm.networld_tick(world);
    return ret >>> 0;
}

/**
 * Throw a Healing Weeball for `slot` (Wipeout).
 * @param {number} world
 * @param {number} slot
 */
export function networld_use_weeball(world, slot) {
    wasm.networld_use_weeball(world, slot);
}

/**
 * Player collision box bounds `[mins x3, maxs x3]` (client-side beam clipping).
 * @returns {Float32Array}
 */
export function player_box_bounds() {
    const ret = wasm.player_box_bounds();
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Player damage hit-volume bounds `[mins x3, maxs x3]` for a hitbox scale
 * (client beam clipping + visible proxy scale). Movement collision uses
 * `player_box_bounds`; `scale` comes from the live `hitbox_scale` rule.
 * @param {number} scale
 * @returns {Float32Array}
 */
export function player_hit_box_bounds(scale) {
    const ret = wasm.player_hit_box_bounds(scale);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * The protocol version compiled into this core (client and server compare it).
 * @returns {number}
 */
export function protocol_version() {
    const ret = wasm.protocol_version();
    return ret >>> 0;
}

/**
 * @param {number} id
 * @param {number} dx
 * @param {number} dy
 */
export function session_add_mouse(id, dx, dy) {
    wasm.session_add_mouse(id, dx, dy);
}

/**
 * @param {number} id
 * @returns {Float32Array}
 */
export function session_angles(id) {
    const ret = wasm.session_angles(id);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Solo hitscan beams, flattened (`Beam::STRIDE` floats each).
 * @param {number} id
 * @returns {Float32Array}
 */
export function session_beams(id) {
    const ret = wasm.session_beams(id);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * cgaz acceleration profile for the current air state (see `Game::cgaz_profile`).
 * @param {number} id
 * @returns {Float32Array}
 */
export function session_cgaz_profile(id) {
    const ret = wasm.session_cgaz_profile(id);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Solo combat HUD state: `[health, armor, weapon, ammo, strong, alive]`.
 * @param {number} id
 * @returns {Float32Array}
 */
export function session_combat(id) {
    const ret = wasm.session_combat(id);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Debug: whether the player currently overlaps a kill trigger (session).
 * @param {number} id
 * @returns {boolean}
 */
export function session_debug_kill(id) {
    const ret = wasm.session_debug_kill(id);
    return ret !== 0;
}

/**
 * Debug: trace the player's box from its origin by `(dx,dy,dz)`.
 * Returns `[fraction, nx, ny, nz, all_solid, start_solid]` (empty if no session).
 * @param {number} id
 * @param {number} dx
 * @param {number} dy
 * @param {number} dz
 * @returns {Float32Array}
 */
export function session_debug_trace(id, dx, dy, dz) {
    const ret = wasm.session_debug_trace(id, dx, dy, dz);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * @param {number} id
 * @param {string} name
 * @returns {boolean}
 */
export function session_delete_slot(id, name) {
    const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.session_delete_slot(id, ptr0, len0);
    return ret !== 0;
}

/**
 * Drain generated commands as a flat `[seq, fwd, right, up, buttons, yaw,
 * pitch]` array (7 ints each).
 * @param {number} id
 * @returns {Int32Array}
 */
export function session_drain_commands(id) {
    const ret = wasm.session_drain_commands(id);
    var v1 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * @param {number} id
 */
export function session_drop(id) {
    wasm.session_drop(id);
}

/**
 * Enter recall (frozen time-travel) at the keyframe nearest the current tick.
 * Returns false if there is no recording.
 * @param {number} id
 * @returns {boolean}
 */
export function session_enter_recall(id) {
    const ret = wasm.session_enter_recall(id);
    return ret !== 0;
}

/**
 * Solo projectiles, flattened (`Entity::STRIDE` floats each).
 * @param {number} id
 * @returns {Float32Array}
 */
export function session_entities(id) {
    const ret = wasm.session_entities(id);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Serialize the current recording for persistence (tagged with `map`).
 * @param {number} id
 * @param {string} map
 * @returns {Uint8Array}
 */
export function session_export_recording(id, map) {
    const ptr0 = passStringToWasm0(map, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.session_export_recording(id, ptr0, len0);
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * @param {number} id
 * @returns {Float32Array}
 */
export function session_eye(id) {
    const ret = wasm.session_eye(id);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Number of server acks resolved without rollback+replay (fast path).
 * @param {number} id
 * @returns {number}
 */
export function session_fast_reconciles(id) {
    const ret = wasm.session_fast_reconciles(id);
    return ret >>> 0;
}

/**
 * Interpolated ghost pose `[x, y, z, yaw, pitch]` at a race time (empty = no ghost).
 * @param {number} id
 * @param {number} race_tick
 * @returns {Float32Array}
 */
export function session_ghost_at(id, race_tick) {
    const ret = wasm.session_ghost_at(id, race_tick);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Hide the ghost (toggle it off).
 * @param {number} id
 */
export function session_ghost_clear(id) {
    wasm.session_ghost_clear(id);
}

/**
 * Flat ghost samples `[race, x, y, z, yaw, pitch]` for persistence.
 * @param {number} id
 * @returns {Float32Array}
 */
export function session_ghost_export(id) {
    const ret = wasm.session_ghost_export(id);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Import flat ghost samples into the live ghost slot (returns false if bad).
 * @param {number} id
 * @param {Float32Array} data
 * @returns {boolean}
 */
export function session_ghost_import(id, data) {
    const ptr0 = passArrayF32ToWasm0(data, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.session_ghost_import(id, ptr0, len0);
    return ret !== 0;
}

/**
 * @param {number} id
 * @returns {number}
 */
export function session_ghost_len(id) {
    const ret = wasm.session_ghost_len(id);
    return ret >>> 0;
}

/**
 * Load a recording previously produced by `session_export_recording`.
 * @param {number} id
 * @param {Uint8Array} data
 * @returns {boolean}
 */
export function session_import_recording(id, data) {
    const ptr0 = passArray8ToWasm0(data, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.session_import_recording(id, ptr0, len0);
    return ret !== 0;
}

/**
 * @param {number} id
 * @returns {boolean}
 */
export function session_is_noclip(id) {
    const ret = wasm.session_is_noclip(id);
    return ret !== 0;
}

/**
 * @param {number} id
 * @returns {boolean}
 */
export function session_is_practice(id) {
    const ret = wasm.session_is_practice(id);
    return ret !== 0;
}

/**
 * @param {number} id
 * @returns {boolean}
 */
export function session_is_recalled(id) {
    const ret = wasm.session_is_recalled(id);
    return ret !== 0;
}

/**
 * Solo item `i` as `[x, y, z, kind, weapon, amount, present, remaining_ms]`
 * into `out` (>=7; the 8th value is written when the buffer has room).
 * @param {number} id
 * @param {number} i
 * @param {Float32Array} out
 */
export function session_item(id, i, out) {
    var ptr0 = passArrayF32ToWasm0(out, wasm.__wbindgen_malloc);
    var len0 = WASM_VECTOR_LEN;
    wasm.session_item(id, i, ptr0, len0, out);
}

/**
 * Solo item pickup count.
 * @param {number} id
 * @returns {number}
 */
export function session_item_count(id) {
    const ret = wasm.session_item_count(id);
    return ret >>> 0;
}

/**
 * Game mode: 0 = Run, 1 = Practice (basic), 2 = Noclip, 3 = Recall.
 * @param {number} id
 * @returns {number}
 */
export function session_mode(id) {
    const ret = wasm.session_mode(id);
    return ret;
}

/**
 * Movement HUD data (8 floats): velocity[3], wishdir[3], speed, accel.
 * @param {number} id
 * @returns {Float32Array}
 */
export function session_movement_hint(id) {
    const ret = wasm.session_movement_hint(id);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * @param {number} id
 * @returns {number}
 */
export function session_mover_count(id) {
    const ret = wasm.session_mover_count(id);
    return ret >>> 0;
}

/**
 * Per-mover render transforms (12 floats each: origin + 3 basis vectors).
 * @param {number} id
 * @returns {Float32Array}
 */
export function session_mover_transforms(id) {
    const ret = wasm.session_mover_transforms(id);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Create a playable session bound to a previously-parsed map (by handle id)
 * at the given spawn index. Returns a new session handle.
 * @param {number} map_id
 * @param {number} spawn_index
 * @returns {number}
 */
export function session_new(map_id, spawn_index) {
    const ret = wasm.session_new(map_id, spawn_index);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0] >>> 0;
}

/**
 * Next command sequence number (commands generated so far).
 * @param {number} id
 * @returns {number}
 */
export function session_next_seq(id) {
    const ret = wasm.session_next_seq(id);
    return ret >>> 0;
}

/**
 * @param {number} id
 * @returns {boolean}
 */
export function session_on_ground(id) {
    const ret = wasm.session_on_ground(id);
    return ret !== 0;
}

/**
 * Player world-space origin (box center, Z-up).
 * @param {number} id
 * @returns {Float32Array}
 */
export function session_origin(id) {
    const ret = wasm.session_origin(id);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Save the current position as the new spawn (only within the start zone).
 * Returns true if the save succeeded.
 * @param {number} id
 * @returns {boolean}
 */
export function session_position_save(id) {
    const ret = wasm.session_position_save(id);
    return ret !== 0;
}

/**
 * Predicted hitscan/melee beam for client fire prediction:
 * `[start x3, end x3, reload_ms]`, or empty for projectile weapons.
 * @param {number} id
 * @param {number} weapon
 * @param {boolean} strong
 * @returns {Float32Array}
 */
export function session_predict_beam(id, weapon, strong) {
    const ret = wasm.session_predict_beam(id, weapon, strong);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Predicted projectile spawn for client fire prediction:
 * `[start x3, velocity x3, grenade, gravity, reload_ms]`, or empty for
 * non-projectile weapons.
 * @param {number} id
 * @param {number} weapon
 * @param {boolean} strong
 * @returns {Float32Array}
 */
export function session_predict_projectile(id, weapon, strong) {
    const ret = wasm.session_predict_projectile(id, weapon, strong);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Live prejump state (pre-race jump/dash/walljump counters exceeded).
 * @param {number} id
 * @returns {boolean}
 */
export function session_prejumped(id) {
    const ret = wasm.session_prejumped(id);
    return ret !== 0;
}

/**
 * @param {number} id
 * @returns {boolean}
 */
export function session_race_finished(id) {
    const ret = wasm.session_race_finished(id);
    return ret !== 0;
}

/**
 * @param {number} id
 * @returns {number}
 */
export function session_race_finished_ticks(id) {
    const ret = wasm.session_race_finished_ticks(id);
    return ret >>> 0;
}

/**
 * Whether the current run was flagged prejumped at the start line.
 * @param {number} id
 * @returns {boolean}
 */
export function session_race_prejumped(id) {
    const ret = wasm.session_race_prejumped(id);
    return ret !== 0;
}

/**
 * @param {number} id
 * @returns {boolean}
 */
export function session_race_running(id) {
    const ret = wasm.session_race_running(id);
    return ret !== 0;
}

/**
 * @param {number} id
 * @returns {Uint32Array}
 */
export function session_race_splits(id) {
    const ret = wasm.session_race_splits(id);
    var v1 = getArrayU32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * @param {number} id
 * @returns {number}
 */
export function session_race_ticks(id) {
    const ret = wasm.session_race_ticks(id);
    return ret >>> 0;
}

/**
 * @param {number} id
 * @returns {number}
 */
export function session_race_total_checkpoints(id) {
    const ret = wasm.session_race_total_checkpoints(id);
    return ret >>> 0;
}

/**
 * @param {number} id
 * @returns {boolean}
 */
export function session_recall_active(id) {
    const ret = wasm.session_recall_active(id);
    return ret !== 0;
}

/**
 * Number of keyframes the recall timeline can step between.
 * @param {number} id
 * @returns {number}
 */
export function session_recall_count(id) {
    const ret = wasm.session_recall_count(id);
    return ret >>> 0;
}

/**
 * Currently selected recall keyframe index.
 * @param {number} id
 * @returns {number}
 */
export function session_recall_index(id) {
    const ret = wasm.session_recall_index(id);
    return ret >>> 0;
}

/**
 * Full-snapshot keyframe ticks (for an efficient scrubber / snapping).
 * @param {number} id
 * @returns {Uint32Array}
 */
export function session_recall_keyframes(id) {
    const ret = wasm.session_recall_keyframes(id);
    var v1 = getArrayU32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Highest recorded tick; the recall scrubber range is `0..=length`.
 * @param {number} id
 * @returns {number}
 */
export function session_recall_length(id) {
    const ret = wasm.session_recall_length(id);
    return ret >>> 0;
}

/**
 * Select a specific recall keyframe index.
 * @param {number} id
 * @param {number} index
 * @returns {boolean}
 */
export function session_recall_set_index(id, index) {
    const ret = wasm.session_recall_set_index(id, index);
    return ret !== 0;
}

/**
 * Teleport to a named position slot.
 * @param {number} id
 * @param {string} name
 * @returns {boolean}
 */
export function session_recall_slot(id, name) {
    const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.session_recall_slot(id, ptr0, len0);
    return ret !== 0;
}

/**
 * Start (or restart) recording the current run for recall / ghosts.
 * @param {number} id
 */
export function session_recall_start(id) {
    wasm.session_recall_start(id);
}

/**
 * Step the recall selection by `delta` keyframes (clamped).
 * @param {number} id
 * @param {number} delta
 * @returns {boolean}
 */
export function session_recall_step(id, delta) {
    const ret = wasm.session_recall_step(id, delta);
    return ret !== 0;
}

/**
 * Stop recording (recall still works on what was already recorded).
 * @param {number} id
 */
export function session_recall_stop(id) {
    wasm.session_recall_stop(id);
}

/**
 * Tick of the selected recall keyframe.
 * @param {number} id
 * @returns {number}
 */
export function session_recall_tick(id) {
    const ret = wasm.session_recall_tick(id);
    return ret >>> 0;
}

/**
 * Reconcile against the server: `ack` (last processed seq) + the server's
 * `PlayerState` bytes. Returns the position correction `[dx, dy, dz]` or empty.
 * @param {number} id
 * @param {number} ack
 * @param {Uint8Array} ps
 * @returns {Float32Array}
 */
export function session_reconcile(id, ack, ps) {
    const ptr0 = passArray8ToWasm0(ps, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.session_reconcile(id, ack, ptr0, len0);
    var v2 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v2;
}

/**
 * Reset the player to the spawn point (race restart).
 * @param {number} id
 */
export function session_reset(id) {
    wasm.session_reset(id);
}

/**
 * Save the current full state under a named slot.
 * @param {number} id
 * @param {string} name
 * @returns {boolean}
 */
export function session_save_slot(id, name) {
    const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.session_save_slot(id, ptr0, len0);
    return ret !== 0;
}

/**
 * Multiplayer: save the current position as the local respawn point (no
 * preRace / on-ground checks); the server is told separately.
 * @param {number} id
 */
export function session_save_spawn_now(id) {
    wasm.session_save_spawn_now(id);
}

/**
 * Choose the solo weapon.
 * @param {number} id
 * @param {number} weapon
 * @param {boolean} strong
 */
export function session_select_weapon(id, weapon, strong) {
    wasm.session_select_weapon(id, weapon, strong);
}

/**
 * Multiplayer prediction: set the solid blockers (other players) so the local
 * player collides with them. Flat `[x,y,z]` triples; empty clears.
 * @param {number} id
 * @param {Float32Array} data
 */
export function session_set_blockers(id, data) {
    const ptr0 = passArrayF32ToWasm0(data, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    wasm.session_set_blockers(id, ptr0, len0);
}

/**
 * CTF flag-carrier speed weight (1 = not carrying). The client mirrors the
 * server's post-movement clamp from the mode + flag snapshot.
 * @param {number} id
 * @param {number} factor
 */
export function session_set_carrier_factor(id, factor) {
    wasm.session_set_carrier_factor(id, factor);
}

/**
 * @param {number} id
 * @param {boolean} forward
 * @param {boolean} back
 * @param {boolean} left
 * @param {boolean} right
 * @param {boolean} jump
 * @param {boolean} crouch
 * @param {boolean} special
 * @param {boolean} attack
 * @param {boolean} attack2
 */
export function session_set_keys(id, forward, back, left, right, jump, crouch, special, attack, attack2) {
    wasm.session_set_keys(id, forward, back, left, right, jump, crouch, special, attack, attack2);
}

/**
 * Run the single-player combat/pickup sim locally even while networked (used
 * while the client is detached in a local practice session).
 * @param {number} id
 * @param {boolean} on
 */
export function session_set_local_combat(id, on) {
    wasm.session_set_local_combat(id, on);
}

/**
 * Set the mode: 0 = Run, 1 = Practice, 2 = Noclip, 3 = Recall.
 * @param {number} id
 * @param {number} mode
 * @returns {boolean}
 */
export function session_set_mode(id, mode) {
    const ret = wasm.session_set_mode(id, mode);
    return ret !== 0;
}

/**
 * @param {number} id
 * @param {number} sens
 * @param {number} yaw_mult
 * @param {number} pitch_mult
 */
export function session_set_mouse_config(id, sens, yaw_mult, pitch_mult) {
    wasm.session_set_mouse_config(id, sens, yaw_mult, pitch_mult);
}

/**
 * Set one movement rule on the client's prediction (see `MoveRules::set`).
 * @param {number} id
 * @param {string} key
 * @param {number} value
 * @returns {boolean}
 */
export function session_set_move_rule(id, key, value) {
    const ptr0 = passStringToWasm0(key, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.session_set_move_rule(id, ptr0, len0, value);
    return ret !== 0;
}

/**
 * Enable/disable client prediction history + outgoing commands (multiplayer).
 * @param {number} id
 * @param {boolean} on
 */
export function session_set_networked(id, on) {
    wasm.session_set_networked(id, on);
}

/**
 * Toggle noclip free-fly (implies practice mode).
 * @param {number} id
 * @param {boolean} on
 */
export function session_set_noclip(id, on) {
    wasm.session_set_noclip(id, on);
}

/**
 * Toggle practice mode (flags the run non-competitive).
 * @param {number} id
 * @param {boolean} on
 */
export function session_set_practice(id, on) {
    wasm.session_set_practice(id, on);
}

/**
 * Enable/disable the race prejump rule (Race/Freestyle only).
 * @param {number} id
 * @param {boolean} on
 */
export function session_set_prejump_rules(id, on) {
    wasm.session_set_prejump_rules(id, on);
}

/**
 * Multiplayer prediction: remote player boxes used to advance movers. Flat
 * `[x,y,z]` triples from the latest snapshot; empty clears.
 * @param {number} id
 * @param {Float32Array} data
 */
export function session_set_remote_boxes(id, data) {
    const ptr0 = passArrayF32ToWasm0(data, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    wasm.session_set_remote_boxes(id, ptr0, len0);
}

/**
 * Set one rule (movement **or** combat) on the local game (solo).
 * @param {number} id
 * @param {string} key
 * @param {number} value
 * @returns {boolean}
 */
export function session_set_rule(id, key, value) {
    const ptr0 = passStringToWasm0(key, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.session_set_rule(id, ptr0, len0, value);
    return ret !== 0;
}

/**
 * Set the mode-level self-damage multiplier on the local session (0 = no
 * self damage; rocket jumps still push).
 * @param {number} id
 * @param {number} scale
 * @returns {boolean}
 */
export function session_set_self_damage_scale(id, scale) {
    const ret = wasm.session_set_self_damage_scale(id, scale);
    return ret !== 0;
}

/**
 * @param {number} id
 * @param {number} sens
 */
export function session_set_sensitivity(id, sens) {
    wasm.session_set_sensitivity(id, sens);
}

/**
 * Multiplayer join: align the player and respawn point with the server spawn.
 * @param {number} id
 * @param {number} x
 * @param {number} y
 * @param {number} z
 * @param {number} yaw
 * @param {number} pitch
 */
export function session_set_spawn(id, x, y, z, yaw, pitch) {
    wasm.session_set_spawn(id, x, y, z, yaw, pitch);
}

/**
 * Set the view from quantized 16-bit angle bits (server: apply a client's view).
 * @param {number} id
 * @param {number} yaw
 * @param {number} pitch
 */
export function session_set_view_bits(id, yaw, pitch) {
    wasm.session_set_view_bits(id, yaw, pitch);
}

/**
 * Online combat: the server owns void deaths; the client must not respawn
 * itself when it falls out of the map (it would fight the death + respawn).
 * @param {number} id
 * @param {boolean} on
 */
export function session_set_void_server_owned(id, on) {
    wasm.session_set_void_server_owned(id, on);
}

/**
 * Named position slot names (insertion order).
 * @param {number} id
 * @returns {string[]}
 */
export function session_slot_names(id) {
    const ret = wasm.session_slot_names(id);
    var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]);
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Spawn a physics ball in the solo session (debug/dev hook). Returns the id.
 * @param {number} id
 * @param {number} x
 * @param {number} y
 * @param {number} z
 * @param {number} vx
 * @param {number} vy
 * @param {number} vz
 * @returns {number}
 */
export function session_spawn_ball(id, x, y, z, vx, vy, vz) {
    const ret = wasm.session_spawn_ball(id, x, y, z, vx, vy, vz);
    return ret >>> 0;
}

/**
 * @param {number} id
 * @returns {number}
 */
export function session_speed(id) {
    const ret = wasm.session_speed(id);
    return ret;
}

/**
 * @param {number} id
 * @returns {number}
 */
export function session_step(id) {
    const ret = wasm.session_step(id);
    return ret >>> 0;
}

/**
 * Teleport the local player to the server's authoritative spawn on join.
 * @param {number} id
 * @param {number} x
 * @param {number} y
 * @param {number} z
 * @param {number} yaw
 * @param {number} pitch
 */
export function session_teleport(id, x, y, z, yaw, pitch) {
    wasm.session_teleport(id, x, y, z, yaw, pitch);
}

/**
 * Debug: throw a physics ball from the player's eye along the view. Returns id.
 * @param {number} id
 * @returns {number}
 */
export function session_throw_ball(id) {
    const ret = wasm.session_throw_ball(id);
    return ret >>> 0;
}

/**
 * The live game tick (index of the current state).
 * @param {number} id
 * @returns {number}
 */
export function session_tick(id) {
    const ret = wasm.session_tick(id);
    return ret >>> 0;
}

/**
 * Trace a small box (`radius`) from `(ox,oy,oz)` to `(ex,ey,ez)` against the
 * world. Returns `[fraction, nx, ny, nz, start_solid]` (empty if no session).
 * For client-local particle collision (presentation only).
 * @param {number} id
 * @param {number} ox
 * @param {number} oy
 * @param {number} oz
 * @param {number} ex
 * @param {number} ey
 * @param {number} ez
 * @param {number} radius
 * @returns {Float32Array}
 */
export function session_trace_ray(id, ox, oy, oz, ex, ey, ez, radius) {
    const ret = wasm.session_trace_ray(id, ox, oy, oz, ex, ey, ez, radius);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Player velocity (Z-up).
 * @param {number} id
 * @returns {Float32Array}
 */
export function session_velocity(id) {
    const ret = wasm.session_velocity(id);
    var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * The current view as `[yaw, pitch]` 16-bit bits (client: send exactly what the
 * local sim used).
 * @param {number} id
 * @returns {Uint16Array}
 */
export function session_view_bits(id) {
    const ret = wasm.session_view_bits(id);
    var v1 = getArrayU16FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 2, 2);
    return v1;
}

/**
 * Whether the server owns out-of-bounds (void) deaths (online combat modes).
 * @param {number} id
 * @returns {boolean}
 */
export function session_void_server_owned(id) {
    const ret = wasm.session_void_server_owned(id);
    return ret !== 0;
}

/**
 * The current solo weapon id.
 * @param {number} id
 * @returns {number}
 */
export function session_weapon(id) {
    const ret = wasm.session_weapon(id);
    return ret;
}

/**
 * Whether `weapon` refires continuously while held (the LG).
 * @param {number} id
 * @param {number} weapon
 * @param {boolean} strong
 * @returns {number}
 */
export function session_weapon_smooth(id, weapon, strong) {
    const ret = wasm.session_weapon_smooth(id, weapon, strong);
    return ret;
}

/**
 * Number of ticks between the live simulation tick and the state recorded in
 * the broadcast snapshot/history. The core records each tick's state **before**
 * incrementing the live tick, so the state players saw is `live_tick - 1`.
 * Server and client use this one value for the cadence label.
 * @returns {number}
 */
export function snapshot_tick_lag() {
    const ret = wasm.snapshot_tick_lag();
    return ret >>> 0;
}

/**
 * The core simulation tick rate (Hz). Exposed so the server/client protocol
 * can advertise and verify the same timebase instead of hardcoding it.
 * @returns {number}
 */
export function tick_rate() {
    const ret = wasm.tick_rate();
    return ret >>> 0;
}

/**
 * Exported to JS: the canonical weapon id -> display name table. The client
 * generator emits `web/src/net/weapons.generated.ts`, so the settings UI and
 * `main.ts` cannot drift from the Rust ids.
 * @returns {string}
 */
export function weapon_schema() {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.weapon_schema();
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Exported to JS: the canonical wire schema.
 * @returns {string}
 */
export function wire_schema() {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.wire_schema();
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}
function __wbg_get_imports() {
    const import0 = {
        __proto__: null,
        __wbg___wbindgen_copy_to_typed_array_88899a52af046901: function(arg0, arg1, arg2) {
            new Uint8Array(arg2.buffer, arg2.byteOffset, arg2.byteLength).set(getArrayU8FromWasm0(arg0, arg1));
        },
        __wbg___wbindgen_debug_string_4687d8d8c2017d52: function(arg0, arg1) {
            const ret = debugString(arg1);
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg_error_9b3cb39b3b4a8f77: function(arg0, arg1) {
            console.error(getStringFromWasm0(arg0, arg1));
        },
        __wbindgen_generic_0000000000000001: function(arg0, arg1) {
            // Cast intrinsic for `Ref(String) -> Externref`.
            const ret = getStringFromWasm0(arg0, arg1);
            return ret;
        },
        __wbindgen_init_externref_table: function() {
            const table = wasm.__wbindgen_externrefs;
            const offset = table.grow(4);
            table.set(0, undefined);
            table.set(offset + 0, undefined);
            table.set(offset + 1, null);
            table.set(offset + 2, true);
            table.set(offset + 3, false);
        },
    };
    return {
        __proto__: null,
        "./webrace_core_bg.js": import0,
    };
}

function debugString(val) {
    // primitive types
    const type = typeof val;
    if (type == 'number' || type == 'boolean' || val == null) {
        return  `${val}`;
    }
    if (type == 'string') {
        return `"${val}"`;
    }
    if (type == 'symbol') {
        const description = val.description;
        if (description == null) {
            return 'Symbol';
        } else {
            return `Symbol(${description})`;
        }
    }
    if (type == 'function') {
        const name = val.name;
        if (typeof name == 'string' && name.length > 0) {
            return `Function(${name})`;
        } else {
            return 'Function';
        }
    }
    // objects
    if (Array.isArray(val)) {
        const length = val.length;
        let debug = '[';
        if (length > 0) {
            debug += debugString(val[0]);
        }
        for(let i = 1; i < length; i++) {
            debug += ', ' + debugString(val[i]);
        }
        debug += ']';
        return debug;
    }
    // Test for built-in
    const builtInMatches = /\[object ([^\]]+)\]/.exec(toString.call(val));
    let className;
    if (builtInMatches && builtInMatches.length > 1) {
        className = builtInMatches[1];
    } else {
        // Failed to match the standard '[object ClassName]'
        return toString.call(val);
    }
    if (className == 'Object') {
        // we're a user defined class or Object
        // JSON.stringify avoids problems with cycles, and is generally much
        // easier than looping through ownProperties of `val`.
        try {
            return 'Object(' + JSON.stringify(val) + ')';
        } catch (_) {
            return 'Object';
        }
    }
    // errors
    if (val instanceof Error) {
        return `${val.name}: ${val.message}\n${val.stack}`;
    }
    // TODO we could test for more things here, like `Set`s and `Map`s.
    return className;
}

function getArrayF32FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getFloat32ArrayMemory0().subarray(ptr / 4, ptr / 4 + len);
}

function getArrayI32FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getInt32ArrayMemory0().subarray(ptr / 4, ptr / 4 + len);
}

function getArrayJsValueFromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    const mem = getDataViewMemory0();
    const result = [];
    for (let i = ptr; i < ptr + 4 * len; i += 4) {
        result.push(wasm.__wbindgen_externrefs.get(mem.getUint32(i, true)));
    }
    wasm.__externref_drop_slice(ptr, len);
    return result;
}

function getArrayU16FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint16ArrayMemory0().subarray(ptr / 2, ptr / 2 + len);
}

function getArrayU32FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint32ArrayMemory0().subarray(ptr / 4, ptr / 4 + len);
}

function getArrayU8FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint8ArrayMemory0().subarray(ptr / 1, ptr / 1 + len);
}

let cachedDataViewMemory0 = null;
function getDataViewMemory0() {
    if (cachedDataViewMemory0 === null || cachedDataViewMemory0.buffer.detached === true || (cachedDataViewMemory0.buffer.detached === undefined && cachedDataViewMemory0.buffer !== wasm.memory.buffer)) {
        cachedDataViewMemory0 = new DataView(wasm.memory.buffer);
    }
    return cachedDataViewMemory0;
}

let cachedFloat32ArrayMemory0 = null;
function getFloat32ArrayMemory0() {
    if (cachedFloat32ArrayMemory0 === null || cachedFloat32ArrayMemory0.byteLength === 0) {
        cachedFloat32ArrayMemory0 = new Float32Array(wasm.memory.buffer);
    }
    return cachedFloat32ArrayMemory0;
}

let cachedInt32ArrayMemory0 = null;
function getInt32ArrayMemory0() {
    if (cachedInt32ArrayMemory0 === null || cachedInt32ArrayMemory0.byteLength === 0) {
        cachedInt32ArrayMemory0 = new Int32Array(wasm.memory.buffer);
    }
    return cachedInt32ArrayMemory0;
}

function getStringFromWasm0(ptr, len) {
    return decodeText(ptr >>> 0, len);
}

let cachedUint16ArrayMemory0 = null;
function getUint16ArrayMemory0() {
    if (cachedUint16ArrayMemory0 === null || cachedUint16ArrayMemory0.byteLength === 0) {
        cachedUint16ArrayMemory0 = new Uint16Array(wasm.memory.buffer);
    }
    return cachedUint16ArrayMemory0;
}

let cachedUint32ArrayMemory0 = null;
function getUint32ArrayMemory0() {
    if (cachedUint32ArrayMemory0 === null || cachedUint32ArrayMemory0.byteLength === 0) {
        cachedUint32ArrayMemory0 = new Uint32Array(wasm.memory.buffer);
    }
    return cachedUint32ArrayMemory0;
}

let cachedUint8ArrayMemory0 = null;
function getUint8ArrayMemory0() {
    if (cachedUint8ArrayMemory0 === null || cachedUint8ArrayMemory0.byteLength === 0) {
        cachedUint8ArrayMemory0 = new Uint8Array(wasm.memory.buffer);
    }
    return cachedUint8ArrayMemory0;
}

function passArray32ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 4, 4) >>> 0;
    getUint32ArrayMemory0().set(arg, ptr / 4);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArray8ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 1, 1) >>> 0;
    getUint8ArrayMemory0().set(arg, ptr / 1);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArrayF32ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 4, 4) >>> 0;
    getFloat32ArrayMemory0().set(arg, ptr / 4);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passStringToWasm0(arg, malloc, realloc) {
    if (realloc === undefined) {
        const buf = cachedTextEncoder.encode(arg);
        const ptr = malloc(buf.length, 1) >>> 0;
        getUint8ArrayMemory0().subarray(ptr, ptr + buf.length).set(buf);
        WASM_VECTOR_LEN = buf.length;
        return ptr;
    }

    let len = arg.length;
    let ptr = malloc(len, 1) >>> 0;

    const mem = getUint8ArrayMemory0();

    let offset = 0;

    for (; offset < len; offset++) {
        const code = arg.charCodeAt(offset);
        if (code > 0x7F) break;
        mem[ptr + offset] = code;
    }
    if (offset !== len) {
        if (offset !== 0) {
            arg = arg.slice(offset);
        }
        ptr = realloc(ptr, len, len = offset + arg.length * 3, 1) >>> 0;
        const view = getUint8ArrayMemory0().subarray(ptr + offset, ptr + len);
        const ret = cachedTextEncoder.encodeInto(arg, view);

        offset += ret.written;
        ptr = realloc(ptr, len, offset, 1) >>> 0;
    }

    WASM_VECTOR_LEN = offset;
    return ptr;
}

function takeFromExternrefTable0(idx) {
    const value = wasm.__wbindgen_externrefs.get(idx);
    wasm.__externref_table_dealloc(idx);
    return value;
}

let cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
cachedTextDecoder.decode();
const MAX_SAFARI_DECODE_BYTES = 2146435072;
let numBytesDecoded = 0;
function decodeText(ptr, len) {
    numBytesDecoded += len;
    if (numBytesDecoded >= MAX_SAFARI_DECODE_BYTES) {
        cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
        cachedTextDecoder.decode();
        numBytesDecoded = len;
    }
    return cachedTextDecoder.decode(getUint8ArrayMemory0().subarray(ptr, ptr + len));
}

const cachedTextEncoder = new TextEncoder();

if (!('encodeInto' in cachedTextEncoder)) {
    cachedTextEncoder.encodeInto = function (arg, view) {
        const buf = cachedTextEncoder.encode(arg);
        view.set(buf);
        return {
            read: arg.length,
            written: buf.length
        };
    };
}

let WASM_VECTOR_LEN = 0;

let wasmModule, wasmInstance, wasm;
function __wbg_finalize_init(instance, module) {
    wasmInstance = instance;
    wasm = instance.exports;
    wasmModule = module;
    cachedDataViewMemory0 = null;
    cachedFloat32ArrayMemory0 = null;
    cachedInt32ArrayMemory0 = null;
    cachedUint16ArrayMemory0 = null;
    cachedUint32ArrayMemory0 = null;
    cachedUint8ArrayMemory0 = null;
    wasm.__wbindgen_start();
    return wasm;
}

async function __wbg_load(module, imports) {
    if (typeof Response === 'function' && module instanceof Response) {
        if (!module.ok) {
            throw new Error(`failed to fetch Wasm: ${module.status} ${module.statusText} fetching '${module.url}'`);
        }

        if (typeof WebAssembly.instantiateStreaming === 'function') {
            try {
                return await WebAssembly.instantiateStreaming(module, imports);
            } catch (e) {
                const validResponse = expectedResponseType(module.type);

                if (validResponse && module.headers.get('Content-Type') !== 'application/wasm') {
                    console.warn("`WebAssembly.instantiateStreaming` failed because your server does not serve Wasm with `application/wasm` MIME type. Falling back to `WebAssembly.instantiate` which is slower. Original error:\n", e);

                } else { throw e; }
            }
        }

        const bytes = await module.arrayBuffer();
        return await WebAssembly.instantiate(bytes, imports);
    } else {
        const instance = await WebAssembly.instantiate(module, imports);

        if (instance instanceof WebAssembly.Instance) {
            return { instance, module };
        } else {
            return instance;
        }
    }

    function expectedResponseType(type) {
        switch (type) {
            case 'basic': case 'cors': case 'default': return true;
        }
        return false;
    }
}

function initSync(module) {
    if (wasm !== undefined) return wasm;


    if (module !== undefined) {
        if (Object.getPrototypeOf(module) === Object.prototype) {
            ({module} = module)
        } else {
            console.warn('using deprecated parameters for `initSync()`; pass a single object instead')
        }
    }

    const imports = __wbg_get_imports();
    if (!(module instanceof WebAssembly.Module)) {
        module = new WebAssembly.Module(module);
    }
    const instance = new WebAssembly.Instance(module, imports);
    return __wbg_finalize_init(instance, module);
}

async function __wbg_init(module_or_path) {
    if (wasm !== undefined) return wasm;


    if (module_or_path !== undefined) {
        if (Object.getPrototypeOf(module_or_path) === Object.prototype) {
            ({module_or_path} = module_or_path)
        } else {
            console.warn('using deprecated parameters for the initialization function; pass a single object instead')
        }
    }

    if (module_or_path === undefined) {
        module_or_path = new URL('webrace_core_bg.wasm', import.meta.url);
    }
    const imports = __wbg_get_imports();

    if (typeof module_or_path === 'string' || (typeof Request === 'function' && module_or_path instanceof Request) || (typeof URL === 'function' && module_or_path instanceof URL)) {
        module_or_path = fetch(module_or_path);
    }

    const { instance, module } = await __wbg_load(await module_or_path, imports);

    return __wbg_finalize_init(instance, module);
}

export { initSync, __wbg_init as default };
